import { api, unwrap } from "@lumberset/api-client";
import { ago, correctionReasons, money } from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Checkbox,
  Container,
  Drawer,
  EmptyState,
  Field,
  Prompt,
  SectionHeader,
  Select,
  Skeleton,
  StatusBadge,
  Tabs,
  Textarea,
  type Tone,
  toast,
} from "@lumberset/ui";
import { AcademicCap, Plus, Sparkles } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import {
  type Lesson,
  type LessonCorrection,
  lessonCorrectionsQuery,
  lessonsQuery,
} from "../../lib/queries-lessons";

export const Route = createFileRoute("/_app/lessons")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(lessonsQuery),
  component: Lessons,
});

type Status = Lesson["status"];
type PassName = Lesson["pass"];

/** The reading passes, as the prompt knows them, with what each one reads. */
const PASSES: { value: PassName; label: string; hint: string }[] = [
  { value: "any", label: "Every pass", hint: "Goes into the instructions of every reading pass." },
  {
    value: "notes",
    label: "Notes",
    hint: "General and structural notes, schedules and the design defaults.",
  },
  {
    value: "floorPlan",
    label: "Floor plans",
    hint: "Walls, openings, and beams or posts drawn on floor plans.",
  },
  {
    value: "heights",
    label: "Plate heights",
    hint: "Plate heights read from sections and elevations.",
  },
  {
    value: "framing",
    label: "Framing",
    hint: "Floor framing, foundation and shear wall plans: joists, beams, posts and hardware.",
  },
  { value: "roof", label: "Roof", hint: "Roof framing and roof plans." },
];
const passLabel = Object.fromEntries(PASSES.map((pass) => [pass.value, pass.label])) as Record<
  PassName,
  string
>;

const STATUSES: { value: Status; label: string }[] = [
  { value: "draft", label: "Drafts" },
  { value: "approved", label: "Approved" },
  { value: "retired", label: "Retired" },
];
const statusTone: Record<Status, Tone> = { draft: "orange", approved: "green", retired: "grey" };
const statusLabel: Record<Status, string> = {
  draft: "Draft",
  approved: "Approved",
  retired: "Retired",
};

const reasonLabel = Object.fromEntries(
  correctionReasons("reviewer").map((reason) => [reason.value, reason.label]),
) as Record<string, string>;

const MAX_TEXT = 300;
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

function Lessons() {
  const { user } = Route.useRouteContext();
  const admin = user.role === "admin";
  const { data } = useSuspenseQuery(lessonsQuery);
  const queryClient = useQueryClient();
  const drafts = data.lessons.filter((lesson) => lesson.status === "draft").length;
  const [status, setStatus] = useState<Status>(drafts > 0 ? "draft" : "approved");
  const [editing, setEditing] = useState<Lesson | "new" | null>(null);

  const suggest = useMutation({
    mutationFn: () => unwrap(api.staff.lessons.suggest.$post()),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["lessons"] });
      setStatus("draft");
      toast.success(
        result.costCents ? `${result.message} Cost ${money(result.costCents)}.` : result.message,
      );
    },
    onError: (error) => toast.error(error.message),
  });

  const shown = data.lessons.filter((lesson) => lesson.status === status);
  const groups = PASSES.map((pass) => ({
    ...pass,
    lessons: shown.filter((lesson) => lesson.pass === pass.value),
  })).filter((group) => group.lessons.length > 0);
  const { suggestions } = data;

  return (
    <>
      <Crumbs trail={[{ label: "Reading lessons" }]} />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Reading lessons"
          description="Short rules our Reviewers learned from correcting readings. An approved lesson goes into the reading prompt of its pass on every new run, under the heading Lessons from our estimators. It guides Reading only: quantities still come from the engine. Anyone on Staff can write a lesson or ask for suggestions; an Admin approves them."
        />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-6 py-3">
          <Tabs value={status} onValueChange={(value) => setStatus(value as Status)}>
            <Tabs.List>
              {STATUSES.map((item) => (
                <Tabs.Trigger key={item.value} value={item.value}>
                  {item.label}{" "}
                  <span className="text-ui-fg-muted">
                    {data.lessons.filter((lesson) => lesson.status === item.value).length}
                  </span>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button
              size="small"
              variant="secondary"
              isLoading={suggest.isPending}
              disabled={!suggestions.available}
              onClick={() => suggest.mutate()}
            >
              <Sparkles /> Suggest from corrections
            </Button>
            <Button size="small" onClick={() => setEditing("new")}>
              <Plus /> Write a lesson
            </Button>
          </div>
        </div>

        {groups.length === 0 ? (
          <EmptyState
            icon={<AcademicCap />}
            title={
              status === "draft"
                ? "No drafts waiting"
                : status === "approved"
                  ? "No approved lessons yet"
                  : "Nothing retired"
            }
            description={
              status === "draft"
                ? "Write a lesson from a correction you keep making, or ask for suggestions from the last 60 days of corrections."
                : status === "approved"
                  ? "Approved lessons go into the reading prompt. An Admin approves drafts."
                  : "Lessons an Admin retires or turns down are kept here."
            }
          />
        ) : (
          groups.map((group) => (
            <section key={group.value} className="divide-y">
              <div className="flex flex-wrap items-baseline gap-x-2 bg-ui-bg-subtle px-6 py-2">
                <span className="txt-compact-small-plus text-ui-fg-base">{group.label}</span>
                <span className="txt-compact-xsmall text-ui-fg-muted">{group.hint}</span>
              </div>
              <ul className="divide-y">
                {group.lessons.map((lesson) => (
                  <LessonRow
                    key={lesson.id}
                    lesson={lesson}
                    admin={admin}
                    me={user.id}
                    onEdit={() => setEditing(lesson)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}

        <p className="txt-compact-xsmall px-6 py-3 text-ui-fg-muted">
          {suggestions.available
            ? `Suggestions read up to 80 corrections from the last 60 days and cost a few cents.${suggestions.spentTodayCents !== null && suggestions.dailyCapCents !== null ? ` Spent today: ${money(suggestions.spentTodayCents)} of ${money(suggestions.dailyCapCents)}.` : ""}`
            : "Suggestions are off: the API has no model key."}
        </p>
      </Container>

      <Drawer open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <Drawer.Content>
          {editing !== null ? (
            <LessonForm
              key={editing === "new" ? "new" : editing.id}
              existing={editing === "new" ? null : editing}
              onClose={() => setEditing(null)}
            />
          ) : null}
        </Drawer.Content>
      </Drawer>
    </>
  );
}

function LessonRow({
  lesson,
  admin,
  me,
  onEdit,
}: {
  lesson: Lesson;
  admin: boolean;
  me: string;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const [retiring, setRetiring] = useState(false);
  const done = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: ["lessons"] });
    toast.success(message);
  };
  const approve = useMutation({
    mutationFn: () => unwrap(api.staff.lessons[":id"].approve.$post({ param: { id: lesson.id } })),
    onSuccess: () => done("Lesson approved. New runs carry it."),
    onError: (error) => toast.error(error.message),
  });
  const retire = useMutation({
    mutationFn: () => unwrap(api.staff.lessons[":id"].retire.$post({ param: { id: lesson.id } })),
    onSuccess: () => {
      setRetiring(false);
      return done(lesson.status === "draft" ? "Draft turned down." : "Lesson retired.");
    },
    onError: (error) => toast.error(error.message),
  });
  const mayEdit = admin || (lesson.status === "draft" && lesson.createdBy?.id === me);

  return (
    <li className="flex flex-col gap-y-2 px-6 py-4">
      <p className="txt-small text-pretty break-words text-ui-fg-base">{lesson.text}</p>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge color={statusTone[lesson.status]}>{statusLabel[lesson.status]}</StatusBadge>
        <Badge>{passLabel[lesson.pass]}</Badge>
        <Badge color={lesson.origin === "suggested" ? "purple" : "grey"}>
          {lesson.origin === "suggested" ? "Suggested" : "Written"}
        </Badge>
        {lesson.approvedAt ? (
          <Badge color="blue">Used by {plural(lesson.runsUsed, "run")} since approval</Badge>
        ) : null}
      </div>
      <p className="txt-compact-xsmall text-pretty text-ui-fg-muted">
        {lesson.origin === "suggested"
          ? "Suggested from corrections, asked for by "
          : "Written by "}
        {lesson.createdBy?.name ?? "a former Staff member"} {ago(lesson.createdAt)}.{" "}
        {lesson.approvedBy && lesson.approvedAt
          ? `Approved by ${lesson.approvedBy.name} ${ago(lesson.approvedAt)}.`
          : "Not approved yet."}{" "}
        {lesson.sourceCorrectionIds.length > 0
          ? `From ${plural(lesson.sourceCorrectionIds.length, "correction")}.`
          : ""}
      </p>
      {mayEdit || admin ? (
        <div className="flex flex-wrap gap-2">
          {mayEdit ? (
            <Button size="small" variant="secondary" onClick={onEdit}>
              Edit
            </Button>
          ) : null}
          {admin && lesson.status !== "approved" ? (
            <Button size="small" isLoading={approve.isPending} onClick={() => approve.mutate()}>
              {lesson.status === "retired" ? "Approve again" : "Approve"}
            </Button>
          ) : null}
          {admin && lesson.status !== "retired" ? (
            <Button size="small" variant="secondary" onClick={() => setRetiring(true)}>
              {lesson.status === "draft" ? "Turn down" : "Retire"}
            </Button>
          ) : null}
        </div>
      ) : null}

      <Prompt open={retiring} onOpenChange={setRetiring}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              {lesson.status === "draft" ? "Turn this draft down?" : "Retire this lesson?"}
            </Prompt.Title>
            <Prompt.Description>
              {lesson.status === "draft"
                ? "It moves to Retired and never reaches the reading prompt. Suggestions will not offer it again."
                : "Runs that start from now on read without it. You can approve it again later."}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep it</Prompt.Cancel>
            <Button
              size="small"
              variant="danger"
              isLoading={retire.isPending}
              onClick={() => retire.mutate()}
            >
              {lesson.status === "draft" ? "Turn down" : "Retire"}
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </li>
  );
}

/** Writes a lesson, or edits one. Keyed by the lesson so its state starts fresh each time. */
function LessonForm({ existing, onClose }: { existing: Lesson | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [pass, setPass] = useState<PassName>(existing?.pass ?? "any");
  const [text, setText] = useState(existing?.text ?? "");
  const [sources, setSources] = useState<string[]>(existing?.sourceCorrectionIds ?? []);
  const { data: recent, isPending } = useQuery(lessonCorrectionsQuery);

  const save = useMutation({
    mutationFn: () => {
      const json = { pass, text, sourceCorrectionIds: sources };
      return existing
        ? unwrap(api.staff.lessons[":id"].$patch({ param: { id: existing.id }, json }))
        : unwrap(api.staff.lessons.$post({ json }));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["lessons"] });
      toast.success(
        existing ? "Lesson saved." : "Lesson saved as a draft for an Admin to approve.",
      );
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate();
  }

  const toggle = (id: string, on: boolean) =>
    setSources((current) =>
      on ? [...new Set([...current, id])] : current.filter((item) => item !== id),
    );
  const listed = new Set(recent?.corrections.map((item) => item.id) ?? []);
  const older = sources.filter((id) => !listed.has(id)).length;
  const length = text.replace(/\s+/g, " ").trim().length;

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>{existing ? "Edit lesson" : "Write a lesson"}</Drawer.Title>
        <Drawer.Description>
          One or two sentences, written as an instruction to the plan reader, general enough for any
          set of drawings. No project, customer or address. It waits as a draft until an Admin
          approves it.
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Pass" hint={PASSES.find((item) => item.value === pass)?.hint}>
          {(control) => (
            <Select value={pass} onValueChange={(value) => setPass(value as PassName)}>
              <Select.Trigger {...control}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {PASSES.map((item) => (
                  <Select.Item key={item.value} value={item.value}>
                    {item.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          )}
        </Field>

        <Field label="Lesson" hint={`${length} of ${MAX_TEXT} characters.`}>
          {(control) => (
            <Textarea
              {...control}
              value={text}
              onChange={(event) => setText(event.target.value)}
              required
              minLength={10}
              maxLength={MAX_TEXT}
              rows={4}
              autoFocus
              placeholder="Plate heights are printed on the building sections; read them there instead of assuming 8 ft."
            />
          )}
        </Field>

        <fieldset className="flex min-w-0 flex-col gap-y-2">
          <legend className="txt-compact-small-plus mb-2 text-ui-fg-base">
            Taught by these corrections{" "}
            <span className="txt-compact-small text-ui-fg-muted">(Optional)</span>
          </legend>
          {isPending ? (
            <div className="flex flex-col gap-y-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-5 w-1/2" />
            </div>
          ) : !recent || recent.corrections.length === 0 ? (
            <p className="txt-small text-ui-fg-subtle">No corrections in the last 60 days.</p>
          ) : (
            <ul className="max-h-80 divide-y divide-ui-border-base overflow-y-auto rounded-lg shadow-borders-base">
              {recent.corrections.map((item) => (
                <CorrectionOption
                  key={item.id}
                  item={item}
                  checked={sources.includes(item.id)}
                  onChange={(on) => toggle(item.id, on)}
                />
              ))}
            </ul>
          )}
          {older > 0 ? (
            <p className="txt-compact-xsmall text-ui-fg-muted">
              Also {plural(older, "older correction")}, kept.
            </p>
          ) : null}
        </fieldset>
      </Drawer.Body>

      <Drawer.Footer>
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={save.isPending}>
          {existing ? "Save" : "Save draft"}
        </Button>
      </Drawer.Footer>
    </form>
  );
}

function CorrectionOption({
  item,
  checked,
  onChange,
}: {
  item: LessonCorrection;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = `correction-${item.id}`;
  return (
    <li>
      <label htmlFor={id} className="flex cursor-pointer items-start gap-x-3 px-4 py-3">
        <Checkbox id={id} checked={checked} onCheckedChange={(value) => onChange(value === true)} />
        <span className="flex min-w-0 flex-col gap-y-0.5">
          <span className="txt-compact-small-plus break-words text-ui-fg-base">
            {item.collection} {item.action}
            {item.fields.length > 0 ? `: ${item.fields.join(", ")}` : ""},{" "}
            {(reasonLabel[item.reason] ?? item.reason).toLowerCase()}
          </span>
          <span className="txt-compact-xsmall text-ui-fg-muted">
            {item.projectName}, by a {item.by === "reviewer" ? "Reviewer" : "Member"},{" "}
            {ago(item.createdAt)}
          </span>
          {item.note ? (
            <span className="txt-small text-pretty break-words text-ui-fg-subtle">{item.note}</span>
          ) : null}
        </span>
      </label>
    </li>
  );
}
